import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { PROJECT_CATEGORIES } from '@/constants/projectCategories';

interface Project {
  id: string;
  title: string;
  category: string;
  image?: string;
  link?: string;
  description?: string;
  created_at: string;
  project_type?: 'link' | 'video';
  video_url?: string;
  thumbnail_url?: string;
}

const ProjectCard = ({ project, index, onClick }: { project: Project; index: number; onClick: () => void }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  return (
    <motion.div 
      ref={cardRef}
      className={`group relative overflow-hidden rounded-xl shadow-lg transition-all duration-500 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      } hover:shadow-xl`}
      style={{ transitionDelay: `${index * 100}ms` }}
      whileHover={{ y: -5 }}
      onClick={onClick}
    >
      <div className="relative">
        <img 
          src={project.project_type === 'video' ? project.thumbnail_url : project.image} 
          alt={project.title} 
          className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          style={{ imageRendering: 'crisp-edges' }}
        />
        {project.project_type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
        )}
      </div>
      <div className="p-6 w-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
        <span className="inline-block px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full mb-2">
          {project.category}
        </span>
        <h3 className="text-black font-bold text-xl mb-2">{project.title}</h3>
        {project.description && (
          <p className="text-gray-700 text-sm mb-3 line-clamp-2">{project.description}</p>
        )}
        <div 
          className="inline-flex items-center justify-center px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
        >
          View Project
        </div>
      </div>
    </motion.div>
  );
};

const ProjectModal = ({ project, isOpen, onClose }: { project: Project | null; isOpen: boolean; onClose: () => void }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!project) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            ref={modalRef}
            className="bg-white rounded-xl overflow-hidden max-w-4xl w-full max-h-[90vh] relative"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          >
            <button
              className="absolute top-4 right-4 z-10 bg-white/80 backdrop-blur-sm p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-white transition-colors"
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6 text-center">
              {project.project_type === 'video' ? (
                <div className="w-full max-h-[50vh] mb-4 rounded-lg overflow-hidden">
                  <iframe
                    width="100%"
                    height="400"
                    src={project.video_url?.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title={project.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded-lg"
                  ></iframe>
                </div>
              ) : (
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full max-h-[50vh] object-contain mb-4 rounded-lg"
                />
              )}
              <h3 className="text-xl font-bold mb-2">{project.title}</h3>
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full mb-4">
                {project.category}
              </span>
              {project.description && (
                <p className="text-gray-600 mb-4">{project.description}</p>
              )}
              {(project.link || project.video_url) && (
                <a
                  href={project.project_type === 'video' ? project.video_url : project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  {project.project_type === 'video' ? 'Watch on YouTube' : 'Visit Project'}
                </a>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Portfolio = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<string>('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(6);
  
  const categories = ['All', ...PROJECT_CATEGORIES];
  
  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(project => project.category === filter);

  // Only show the limited number of projects
  const displayedProjects = filteredProjects.slice(0, displayCount);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleShowMore = () => {
    setDisplayCount(prev => prev + 6);
  };

  const handleShowLess = () => {
    setDisplayCount(6);
  };
  
  return (
    <section ref={sectionRef} className="py-20 px-6 bg-gray-50" id="portfolio">
      <div className="container mx-auto">
        <h2 className="section-heading">
          Our <span className="text-blue-500">Portfolio</span>
        </h2>
        <p className="section-subheading">
          Check out some of our recent projects and see how we've helped our clients achieve their goals.
        </p>
        {/* Filter Categories */}
        <motion.div 
          className="flex flex-wrap justify-center gap-2 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          {categories.map((category, index) => (
            <motion.button
              key={index}
              onClick={() => {
                setFilter(category);
                setDisplayCount(6); // Reset display count when changing filter
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === category 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {displayedProjects.map((project, index) => (
                <ProjectCard 
                  key={project.id} 
                  project={project} 
                  index={index}
                  onClick={() => handleProjectClick(project)}
                />
              ))}
            </div>
            {/* Show More / Show Less Buttons */}
            {filteredProjects.length > 6 && (
              <div className="mt-12 text-center space-x-4">
                {displayCount < filteredProjects.length && (
                  <button
                    onClick={handleShowMore}
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                  >
                    Show More ({filteredProjects.length - displayCount} remaining)
                  </button>
                )}
                {displayCount > 6 && (
                  <button
                    onClick={handleShowLess}
                    className="px-6 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
                  >
                    Show Less
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {!loading && displayedProjects.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            No projects found for the selected category.
          </div>
        )}
        <div className="mt-16 text-center">
          <Link
            to="/portfolio"
            className="inline-block px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
            aria-label="View our complete portfolio of projects"
          >
            View All Projects
          </Link>
        </div>
      </div>
      {/* Project Modal */}
      <ProjectModal 
        project={selectedProject} 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </section>
  );
};

export default Portfolio; 